/**
 * Master Color Palette (V2)
 *
 * This file defines the foundational color scales (primitives) from Figma
 * and the semantic color mappings used throughout the application to 
 * maintain backward compatibility.
 */

const primitives = {
	primary: {
		50: '#FFF3EB',
		100: '#FFE4D1',
		200: '#FFC9A3',
		300: '#FF9B5C',
		400: '#FF7A2F',
		500: '#F26419',
		600: '#E55300',
		700: '#C24700',
		800: '#9C3A00',
		900: '#7A2E00',
	},
	gray: {
		50: '#F9FAFB',
		100: '#F3F4F6',
		200: '#E5E7EA',
		300: '#D2D5DB',
		400: '#9EA2AE',
		500: '#6D717F',
		600: '#4D5461',
		700: '#394050',
		800: '#212936',
		900: '#131927',
	},
	blue: {
		50: '#EAF2FF',
		100: '#CFE0FF',
		200: '#A9C7FF',
		300: '#7FAAFF',
		400: '#4F8CFF',
		500: '#116DFF',
		600: '#0F61E6',
		700: '#0C52BF',
		800: '#0A4399',
		900: '#073066',
	},
	yellow: {
		50: '#FFF7E6',
		100: '#FFE5B0',
		200: '#FFD88A',
		300: '#FFC654',
		400: '#FFBB33',
		500: '#FFAA00',
		600: '#E89B00',
		700: '#B57900',
		800: '#8C5E00',
		900: '#6B4700',							
	},
	red: {
		50: '#FDECEC',
		100: '#FAC5C3',
		200: '#F7A9A7',
		300: '#F4827E',
		400: '#F16965',
		500: '#EE443F',
		600: '#D93E39',
		700: '#A9302D',
		800: '#832523',
		900: '#641D1A',
	},
	green: {
		50: '#ECFDF5',
		100: '#C5E9CD',
		200: '#A9DEB4',
		300: '#81CF92',
		400: '#69C57D',
		500: '#43B75D',
		600: '#3DA755',
		700: '#308242',
		800: '#256533',
		900: '#1C4D27',
	},
}

export const COLORS = {
	...primitives,

	// ── brand ──
	blue: {
		...primitives.blue,
		default:  primitives.blue[500],
		subtle:   primitives.blue[50],
		muted:    primitives.blue[200],
		emphasis: primitives.blue[500],
	},

	// ── semantic ──
	green: {
		...primitives.green,
		default:  primitives.green[500],
		subtle:   primitives.green[50],
		muted:    primitives.green[200],
		emphasis: primitives.green[600],
	},
	red: {
		...primitives.red,
		default:  primitives.red[500],
		subtle:   primitives.red[50],
		muted:    primitives.red[200],
		emphasis: primitives.red[700],
	},
	yellow: {
		...primitives.yellow,
		default:  primitives.yellow[500],
		subtle:   primitives.yellow[50],
		muted:    primitives.yellow[200],
		emphasis: primitives.yellow[700],
	},

	// ── Legacy fallbacks ──
	orange: {
		default:  '#ffa07a',
		subtle:   '#ffa07a21',
		muted:    '#ffb899',
		emphasis: '#e8845a',
	},
	purple: {
		default:  '#9747FF',
		subtle:   '#F5F3FF',
		muted:    '#C4B5FD',
		emphasis: '#6D28D9',
	},

	// ── unique app colors ──
	vehicleGreen: {
		default:  '#3fd481',
		subtle:   '#f0fdf4',
		muted:    '#86efac',
		emphasis: '#16a34a',
	},
	celeste: {
		default:  '#0092B8',
		subtle:   '#ECFEFF',
		muted:    '#A2F4FD',
		emphasis: '#0074a0',
	},
	fog: {
		default:  '#099E56',
		subtle:   '#F3FFF9',
		muted:    '#C8FEE4',
		emphasis: '#077a43',
	},
	pink: {
		default:  '#FEF2F2',
		subtle:   '#FFD2D2',
		muted:    '#fca5a5',
		emphasis: '#f87171',
	},
	beige: {
		default:  '#F5EFE6',
		subtle:   '#E8DFCA',
		muted:    '#d4c5aa',
		emphasis: '#c2ae8e',
	},
	chat: {
		yellow: '#fde9a8',
		blue:   '#e8f3fa',
	},

	// ── neutrals ──
	neutral: {
		...primitives.gray,
		0:   '#FFFFFF',
		100: primitives.gray[100],
		200: primitives.gray[200],
		300: primitives.gray[300],
		400: primitives.gray[400],
		500: primitives.gray[500],
		600: primitives.gray[600],
		700: primitives.gray[700],
		800: primitives.gray[800],
		900: primitives.gray[900],
	},

	// ── layout constants ──
	navy:  '#001F3A',
	white: '#FFFFFF',
	black: '#1D1B1B', 
	dashboardBg: '#ECEFF3', 
}
